import React, { useState } from 'react';
import { Check, Loader2, Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useDemo } from '../../contexts/DemoContext';
import type { Complaint } from '../../types/notice';
import { maskMobile, toBn } from '../../utils/bangla';
const steps = ['অভিযোগ গ্রহণ', 'পর্যালোচনাধীন', 'ব্যবস্থা গ্রহণ', 'নিষ্পত্তি'];
const statusIndex: Record<Complaint['status'], number> = {
  নতুন: 0,
  পর্যালোচনাধীন: 1,
  'ব্যবস্থা গ্রহণ': 2,
  নিষ্পত্তিকৃত: 3
};
export function ComplaintTracking() {
  const [params] = useSearchParams();
  const {
    complaints
  } = useDemo();
  const [id, setId] = useState(params.get('id') ?? '');
  const [stage, setStage] = useState<'form' | 'otp' | 'result'>('form');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const found = complaints.find(c => c.id.toUpperCase() === id.trim().toUpperCase());
  const search = (e: React.FormEvent) => {
    e.preventDefault();
    if (!found) return setError('এই নম্বরে কোনো অভিযোগ পাওয়া যায়নি। নম্বরটি পুনরায় যাচাই করুন।');
    setError('');
    setStage('otp');
  };
  const verify = () => {
    if (otp.length !== 6) return setError('৬ অঙ্কের OTP প্রদান করুন');
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setError('');
      setStage('result');
    }, 700);
  };
  const idx = found ? statusIndex[found.status] : 0;
  return <div className="mx-auto max-w-2xl px-4 py-8" data-id="element-1947" data-mp="1ubqogcv9h8l2">
      <h1 className="text-2xl font-bold text-[#003B71]" data-id="element-1948" data-mp="1pdfdtzx56u1s">অভিযোগের অবস্থা দেখুন</h1>
      <p className="mt-1 text-sm text-slate-600" data-id="element-1949" data-mp="1keufy75d4mgi">ট্র্যাকিং নম্বর ও নিবন্ধিত মোবাইল যাচাইয়ের মাধ্যমে</p>

      {stage === 'form' && <form onSubmit={search} className="mt-6 rounded-xl border border-border bg-white p-6" data-id="element-1950" data-mp="3jqx4vwwra91k">
          <label className="block" data-id="element-1951" data-mp="3etwzlo7qdabi">
            <span className="mb-1.5 block font-medium" data-id="element-1952" data-mp="3etop22z2yh3h">অভিযোগ নম্বর</span>
            <Input value={id} onChange={e => setId(e.target.value)} placeholder="DMP-FR-2026-000145" className="h-12 font-mono text-base" data-id="element-1953" data-mp="1d2xvtcv4re0m" />
          </label>
          {error && <p className="mt-2 text-sm text-[#D93036]" data-id="element-1954" data-mp="1361w9wn7m287">{error}</p>}
          <Button type="submit" className="mt-4 h-12 w-full" data-id="element-1955" data-mp="133fgxau2uiff"><Search className="h-4 w-4" data-id="element-1956" data-mp="185o9w76y9khj" /> অনুসন্ধান করুন</Button>
          <button type="button" onClick={() => setId(complaints[0].id)} className="mt-3 text-sm text-[#1769AA] hover:underline" data-id="element-1957" data-mp="1vu7h0je9jr81">ডেমো: সর্বশেষ অভিযোগ নম্বর বসান</button>
        </form>}

      {stage === 'otp' && found && <div className="mt-6 rounded-xl border border-border bg-white p-6" data-id="element-1958" data-mp="1lwszshkimyeo">
          <div className="font-medium" data-id="element-1959" data-mp="1quujn4prn1r7">মোবাইল যাচাইকরণ</div>
          <p className="mt-1 text-sm text-slate-600" data-id="element-1960" data-mp="1qt9qnjj3nqvg">{maskMobile(found.mobile)} নম্বরে পাঠানো ৬ অঙ্কের OTP লিখুন।</p>
          <Input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="••••••" className="mt-4 h-12 text-center font-mono text-xl tracking-[0.5em]" data-id="element-1961" data-mp="aj7fb9plpkzh" />
          <p className="mt-1 text-xs text-slate-500" data-id="element-1962" data-mp="2oom2znbzcrcu">ডেমো: যেকোনো ৬ অঙ্ক</p>
          {error && <p className="mt-2 text-sm text-[#D93036]" data-id="element-1963" data-mp="2oqzvppj6trh8">{error}</p>}
          <Button className="mt-4 h-12 w-full" onClick={verify} disabled={loading} data-id="element-1964" data-mp="32n3kh3bal7mb">{loading ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-1965" data-mp="2ynutu7bjtpuz" /> : null} যাচাই করুন</Button>
        </div>}

      {stage === 'result' && found && <div className="mt-6 rounded-xl border border-border bg-white p-6" data-id="element-1966" data-mp="2av4gpurwt0j4">
          <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-1967" data-mp="25zp9vmsbk4w7">
            <div className="font-mono text-lg font-bold text-[#003B71]" data-id="element-1968" data-mp="2jqkiqz2tilzs">{found.id}</div>
            <div className="text-sm text-slate-500" data-id="element-1969" data-mp="2eykckow5mwur">{found.type} • {found.date}</div>
          </div>
          <ol className="mt-6" data-id="element-1970" data-mp="1r8gbao6fv96x">
            {steps.map((s, i) => {
          const done = i <= idx;
          return <li key={s} className="relative flex gap-4 pb-6 last:pb-0" data-id="element-1971" data-mp="1n7ea41syon2b" data-mpi={i}>
                  {i < steps.length - 1 && <span className={`absolute left-[15px] top-8 h-[calc(100%-24px)] w-0.5 ${i < idx ? 'bg-[#16834A]' : 'bg-slate-200'}`} data-id="element-1972" data-mp="1zyoyrkxs1ami" />}
                  <span className={`z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${done ? 'bg-[#16834A] text-white' : 'border-2 border-slate-200 bg-white text-slate-400'}`} data-id="element-1973" data-mp="29vbig6ed9vmy">{done ? <Check className="h-4 w-4" data-id="element-1974" data-mp="2enl0z1d5jtf7" /> : toBn(i + 1)}</span>
                  <div data-id="element-1975" data-mp="2jkbm433tdf7h">
                    <div className={`font-semibold ${done ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-1976" data-mp="2jn7ezd0t1dp2">{s}</div>
                    <div className="text-sm text-slate-500" data-id="element-1977" data-mp="2sljey5nk9nu6">
                      {i === 0 && `অভিযোগ গৃহীত — ${found.date}`}
                      {i === 1 && (done ? `দায়িত্বপ্রাপ্ত: ${found.assignedTo || 'ডিএমপি মনিটরিং সেল'}` : 'অপেক্ষমাণ')}
                      {i === 2 && (done ? 'প্রয়োজনীয় ব্যবস্থা গ্রহণ করা হচ্ছে' : 'অপেক্ষমাণ')}
                      {i === 3 && (done ? 'অভিযোগ নিষ্পত্তি হয়েছে' : 'অপেক্ষমাণ')}
                    </div>
                  </div>
                </li>;
        })}
          </ol>
          {found.remarks[0] && <div className="mt-5 rounded-lg bg-[#F4F7FB] p-3 text-sm" data-id="element-1978" data-mp="qusspzi13l0t"><span className="font-semibold" data-id="element-1979" data-mp="lwr8vccs3hoa">সর্বশেষ হালনাগাদ: </span>{found.remarks[0].text}</div>}
        </div>}
    </div>;
}